import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, Users, TrendingUp, CheckCircle, Gift, Sparkles,
  BarChart3, Globe, Rocket, ArrowRight, Star, Zap, Target,
  Clock, Shield, Award, ChevronRight, ExternalLink
} from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

// Template configurations for recruitment pages - All using brand colors (blue/grey/white)
const RECRUITMENT_TEMPLATES = {
  default: {
    name: 'Professional',
    headline: 'Turn Your Network Into Passive Income',
    subheadline: 'Join our affiliate program and earn up to $1,600 per referral',
    color: 'blue',
    gradient: 'from-blue-900 via-blue-800 to-blue-700'
  },
  'money-maker': {
    name: 'Money Maker',
    headline: 'Make Money While You Sleep',
    subheadline: 'Up to $1,600 per sale + 5% recurring commissions forever',
    color: 'blue',
    gradient: 'from-slate-800 via-slate-700 to-blue-800'
  },
  'side-hustle': {
    name: 'Side Hustle',
    headline: 'The Easiest Side Hustle Ever',
    subheadline: 'So simple anyone can do it. We give you EVERYTHING.',
    color: 'blue',
    gradient: 'from-blue-800 via-indigo-800 to-blue-900'
  },
  'influencer': {
    name: 'Influencer',
    headline: 'Monetize Your Audience',
    subheadline: 'Premium commissions for content creators.',
    color: 'slate',
    gradient: 'from-slate-900 via-slate-800 to-blue-900'
  },
  'business': {
    name: 'Business Pro',
    headline: 'Add a Revenue Stream to Your Business',
    subheadline: 'Perfect for agencies, consultants & business coaches',
    color: 'slate',
    gradient: 'from-slate-900 via-blue-900 to-slate-800'
  }
};

export default function AffiliateRecruitPage() {
  const params = useParams();
  const affiliateCode = params.affiliateCode || localStorage.getItem('affiliate_code');
  const template = params.template || 'default';
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const templateConfig = RECRUITMENT_TEMPLATES[template] || RECRUITMENT_TEMPLATES.default;

  useEffect(() => {
    const fetchReferrer = async () => {
      try {
        const response = await axios.get(`${API}/affiliate/public/${affiliateCode}`);
        if (response.data.success) {
          setReferrer(response.data.affiliate);
        }
      } catch (error) {
        console.error('Error fetching referrer:', error);
      } finally {
        setLoading(false);
      }
    };

    if (affiliateCode) {
      fetchReferrer();
      // Track page view
      axios.post(`${API}/analytics/page-view`, {
        affiliate_code: affiliateCode,
        template: `recruit-${template}`,
        source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
        referrer: document.referrer
      }).catch(() => {});
      
      // Track OG image impression for A/B testing
      axios.post(`${API}/og/track/impression`, {
        affiliate_code: affiliateCode,
        template: template,
        page_type: 'recruit',
        source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
        referrer: document.referrer
      }).catch(() => {});
    }
  }, [affiliateCode, template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Determine signup source from URL parameters or referrer
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source') || '';
      
      // Map UTM sources to readable names
      let signupSource = 'direct';
      if (utmSource) {
        signupSource = utmSource.toLowerCase();
      } else if (document.referrer) {
        const referrerHost = new URL(document.referrer).hostname.toLowerCase();
        if (referrerHost.includes('linkedin')) signupSource = 'linkedin';
        else if (referrerHost.includes('facebook')) signupSource = 'facebook';
        else if (referrerHost.includes('instagram')) signupSource = 'instagram';
        else if (referrerHost.includes('twitter') || referrerHost.includes('x.com')) signupSource = 'twitter';
        else if (referrerHost.includes('google')) signupSource = 'google';
      }
      
      await axios.post(`${API}/affiliate/quick-apply`, {
        name: formData.name,
        email: formData.email,
        landing_page: `recruit-${template}`,
        referred_by: affiliateCode,
        source: 'recruitment_page',
        signup_source: signupSource
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ogImageUrl = `${API}/og/recruit/${affiliateCode}/${template}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className={`relative bg-gradient-to-br ${templateConfig.gradient} text-white overflow-hidden`}>
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="text-center">
              {referrer && (
                <Badge className="mb-4 bg-white/20 text-white border-white/30">
                  👋 Invited by {referrer.name?.split(' ')[0] || 'a partner'}
                </Badge>
              )}
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white">
                {templateConfig.headline}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                {templateConfig.subheadline}
              </p>

              {/* Commission Stats */}
              <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto mb-8">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
                  <p className="text-2xl sm:text-3xl font-bold text-white">10-25%</p>
                  <p className="text-xs sm:text-sm text-white/80">First Sale</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
                  <p className="text-2xl sm:text-3xl font-bold text-white">$30-75</p>
                  <p className="text-xs sm:text-sm text-white/80">Monthly/Referral</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
                  <p className="text-2xl sm:text-3xl font-bold text-white">10%</p>
                  <p className="text-xs sm:text-sm text-white/80">Team Bonus</p>
                </div>
              </div>

              <a href="#apply" className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-slate-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105">
                Apply Now - It's Free
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-slate-900">
              Everything You Get as an Affiliate
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              We give you all the tools you need to succeed. No experience required.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: <Globe className="h-8 w-8" />, title: '5 Landing Page Templates', desc: 'Professional, SEO-optimized pages with your tracking code built-in' },
                { icon: <BarChart3 className="h-8 w-8" />, title: 'Full Analytics Dashboard', desc: 'Track views, clicks, conversions, bounce rates & more in real-time' },
                { icon: <Sparkles className="h-8 w-8" />, title: 'Ready-Made Social Posts', desc: 'Copy-paste content for LinkedIn, Facebook, Instagram & Twitter' },
                { icon: <Target className="h-8 w-8" />, title: 'AI-Generated OG Images', desc: 'Eye-catching preview images for social media shares' },
                { icon: <Users className="h-8 w-8" />, title: 'Simple Lead CRM', desc: 'Track and manage your leads with email campaign tools' },
                { icon: <Gift className="h-8 w-8" />, title: 'Two-Tier Commissions', desc: 'Recruit affiliates and earn 10% of their commissions forever' }
              ].map((item, i) => (
                <Card key={i} className="border-2 border-slate-100 hover:border-blue-200 transition-colors">
                  <CardContent className="pt-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${templateConfig.gradient} text-white flex items-center justify-center mb-4`}>
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-slate-900">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Commission Breakdown */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-slate-900">
              💰 How Much Can You Earn?
            </h2>
            <p className="text-slate-600 text-center mb-12">
              Real numbers, real potential. No caps on your earnings.
            </p>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
              <div className={`bg-gradient-to-r ${templateConfig.gradient} p-6`}>
                <h3 className="text-xl font-bold text-white">Commission Structure</h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Tiered Commission Structure */}
                <div className="space-y-3">
                  <p className="font-bold text-slate-900 mb-3">Tiered Commission Rates (First Sale + Monthly Recurring)</p>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                      <span className="font-medium text-slate-700">Starter</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-slate-700">10% + $30</span>
                      <span className="text-xs text-slate-500 ml-1">/referral/month</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="font-medium text-blue-700">Growth</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-blue-700">15% + $45</span>
                      <span className="text-xs text-blue-500 ml-1">/referral/month</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                      <span className="font-medium text-indigo-700">Professional</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-indigo-700">20% + $60</span>
                      <span className="text-xs text-indigo-500 ml-1">/referral/month</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="font-medium text-amber-700">Elite Partner</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-amber-700">25% + $75</span>
                      <span className="text-xs text-amber-500 ml-1">/referral/month</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200 mt-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="font-bold text-slate-900">Team Bonus (2nd Tier)</p>
                      <p className="text-sm text-slate-600">Earn from affiliates you recruit</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">10%</p>
                    <p className="text-sm text-slate-500">Of their earnings</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Example Earnings */}
            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                <Star className="h-5 w-5" />
                Example: Monthly Earnings Potential
              </h4>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-xl p-4 border border-blue-200">
                  <p className="text-sm text-slate-600 mb-1">5 sales/month</p>
                  <p className="text-2xl font-bold text-blue-600">$1,500+</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-blue-200">
                  <p className="text-sm text-slate-600 mb-1">10 sales/month</p>
                  <p className="text-2xl font-bold text-blue-600">$3,000+</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-blue-200">
                  <p className="text-sm text-slate-600 mb-1">20 sales/month</p>
                  <p className="text-2xl font-bold text-blue-600">$6,000+</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12 text-slate-900">
              How It Works (3 Simple Steps)
            </h2>

            <div className="space-y-6">
              {[
                { step: 1, title: 'Apply & Get Approved', desc: 'Fill out the quick form below. Most applications are approved within 24 hours.', icon: <CheckCircle /> },
                { step: 2, title: 'Share Your Links', desc: 'Copy your unique landing pages and share them on social media, email, or anywhere.', icon: <Globe /> },
                { step: 3, title: 'Earn Commissions', desc: 'When someone signs up through your link, you earn. We handle everything else.', icon: <DollarSign /> }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${templateConfig.gradient} text-white flex items-center justify-center font-bold text-lg shrink-0`}>
                    {item.step}
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h3 className="font-bold text-lg text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section id="apply" className={`py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br ${templateConfig.gradient}`}>
          <div className="max-w-xl mx-auto">
            <Card className="shadow-2xl">
              <CardContent className="pt-8 pb-8">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-10 w-10 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">You're In!</h3>
                    <p className="text-slate-600 mb-4">
                      Check your email for login instructions. Welcome to the team!
                    </p>
                    <p className="text-sm text-slate-500">
                      {referrer && `Referred by: ${referrer.name}`}
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-center mb-2 text-slate-900">
                      Apply Now - Free Forever
                    </h3>
                    <p className="text-center text-slate-600 mb-6">
                      Takes 30 seconds. No credit card required.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Input
                          type="text"
                          placeholder="Your Full Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          placeholder="Your Email Address"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="h-12"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className={`w-full h-12 text-lg font-bold bg-gradient-to-r ${templateConfig.gradient} hover:opacity-90`}
                      >
                        {submitting ? 'Submitting...' : 'Start Earning Today →'}
                      </Button>
                    </form>

                    <div className="mt-6 flex items-center justify-center gap-6 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Shield className="h-4 w-4" /> Free to join
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" /> Instant approval
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} UPost. All rights reserved.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Affiliate earnings vary based on individual effort and results.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
